/**
 * Raund-v2 スペーシングスケール
 *
 * Figma の `grid_spacing.figma` から抽出。
 * ベース単位 8px の倍数で構成されたグリッドシステム。
 *
 * ## 使い分け
 * - `spacingScale` — px 数値。E2E テスト・計算・CSS-in-JS など Mantine 非依存の場面で使用
 * - `raundSpacing` — rem 文字列。Mantine の `theme.spacing` に渡す用
 *
 * @example
 * // E2E テストでピクセル値を検証
 * import { spacingScale } from '@/theme/spacing'
 * expect(element).toHaveStyle({ padding: `${spacingScale.md}px` })  // 24
 *
 * // Mantine コンポーネントでセマンティックキーを使用
 * <Box p="md" m="lg" />
 * <Stack gap="2xl" />
 */

/** 8px グリッドのベース単位 (px) */
export const SPACING_BASE = 8

/**
 * スペーシングスケール — px 数値
 *
 * | キー  | px  | 8px の倍数 |
 * |-------|-----|-----------|
 * | xs    |   8 | × 1       |
 * | sm    |  16 | × 2       |
 * | md    |  24 | × 3       |
 * | lg    |  32 | × 4       |
 * | xl    |  40 | × 5       |
 * | 2xl   |  48 | × 6       |
 * | 3xl   |  64 | × 8       |
 * | 4xl   |  80 | × 10      |
 * | 5xl   |  96 | × 12      |
 */
export const spacingScale = {
  xs: 8,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 40,
  '2xl': 48,
  '3xl': 64,
  '4xl': 80,
  '5xl': 96,
} as const satisfies Record<string, number>

export type SpacingKey = keyof typeof spacingScale

/**
 * スペーシングスケール — rem 文字列 (Mantine `theme.spacing` 用)
 *
 * 1rem = 16px を前提とした rem 変換値。
 */
export const raundSpacing = {
  xs: '0.5rem', //  8px
  sm: '1rem', // 16px
  md: '1.5rem', // 24px
  lg: '2rem', // 32px
  xl: '2.5rem', // 40px
  '2xl': '3rem', // 48px
  '3xl': '4rem', // 64px
  '4xl': '5rem', // 80px
  '5xl': '6rem', // 96px
} as const satisfies Record<SpacingKey, string>
